/**
 * SharePoint API Client Hook
 * Makes authenticated calls to the SharePoint Q&A service using Bearer JWT.
 * Uses the centralized useTokenManager for token lifecycle.
 */
import { useCallback, useMemo } from "react";

import { useTokenManager } from "./useTokenManager";
import { getConfig } from "../config";



export class SharePointApiError extends Error {
  public status: number;
  public retryAfter: number | null;
  constructor(message: string, status: number, retryAfter: number | null = null) {
    super(message);
    this.name = "SharePointApiError";
    this.status = status;
    this.retryAfter = retryAfter;
  }
}

export function useSharePointApiClient() {
  const tokenManager = useTokenManager();

  const buildHeaders = useCallback(
    async (forceRefresh = false): Promise<HeadersInit> => {
      const token = await tokenManager.getToken(forceRefresh);
      return {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };
    },
    [tokenManager],
  );

  const fetchWithBearer = useCallback(
    async <T>(path: string, options: RequestInit = {}): Promise<T> => {
      const headers = await buildHeaders();
      const spBase = getConfig().sharepointServiceUrl || "https://sharepoint-service.redmoss-1c94587b.eastus2.azurecontainerapps.io";
      const url = `${spBase}${path}`;

      let response = await fetch(url, { ...options, headers });

      if (response.status === 401) {
        const retryHeaders = await buildHeaders(true);
        response = await fetch(url, { ...options, headers: retryHeaders });
      }

      if (!response.ok) {
        let detail = `HTTP ${response.status}`;
        try {
          const body = await response.json();
          detail = body.detail || detail;
        } catch {
          // non-JSON error body
        }
        const retryAfterVal = response.headers.get("Retry-After");
        const retryAfter = retryAfterVal ? parseInt(retryAfterVal, 10) || null : null;
        throw new SharePointApiError(detail, response.status, retryAfter);
      }

      if (response.status === 204) return {} as T;
      return response.json();
    },
    [buildHeaders],
  );

  return useMemo(
    () => ({
      get: <T>(path: string) => fetchWithBearer<T>(path, { method: "GET" }),
      post: <T>(path: string, body?: unknown) =>
        fetchWithBearer<T>(path, {
          method: "POST",
          body: body ? JSON.stringify(body) : undefined,
        }),
    }),
    [fetchWithBearer],
  );
}

export default useSharePointApiClient;
